import { useEffect, useState } from 'react';
import { Loader2, Tag, Wand2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { PageShell } from '@/components/layout/PageShell';
import { EmptyState } from '@/components/ui/EmptyState';
import { DRE_CONFIG, type DreLine } from '@/hooks/useDREDetailed';

interface Category {
  id:       string;
  name:     string;
  kind:     'revenue' | 'expense';
  dre_line: DreLine | null;
}

const DRE_OPTIONS_REVENUE = (Object.entries(DRE_CONFIG) as [DreLine, typeof DRE_CONFIG[DreLine]][])
  .filter(([, c]) => c.sign === 1)
  .map(([k, c]) => ({ value: k, label: c.label }));

const DRE_OPTIONS_EXPENSE = (Object.entries(DRE_CONFIG) as [DreLine, typeof DRE_CONFIG[DreLine]][])
  .filter(([, c]) => c.sign === -1)
  .map(([k, c]) => ({ value: k, label: c.label }));

export function CategoryMapping() {
  const { tenantId } = useTenant();
  const [cats, setCats]     = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchErr, setFetchErr] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestMsg, setSuggestMsg] = useState<string | null>(null);
  const [saved, setSaved]   = useState<string | null>(null);

  const fetch = async () => {
    if (!tenantId) return;
    setLoading(true);
    setFetchErr(null);
    const { data, error } = await supabase
      .from('categories')
      .select('id, name, kind, dre_line')
      .eq('tenant_id', tenantId)
      .order('kind')
      .order('name');
    if (error) setFetchErr('Erro ao carregar categorias. Tente recarregar a página.');
    else setCats((data as unknown as Category[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { void fetch(); }, [tenantId]);

  async function updateDreLine(catId: string, dreLine: DreLine | null) {
    setSaving(catId);
    setSaveErr(null);
    const { error } = await supabase
      .from('categories')
      .update({ dre_line: dreLine } as never)
      .eq('id', catId);
    if (error) {
      setSaveErr('Erro ao salvar mapeamento. Tente novamente.');
    } else {
      setCats((prev) => prev.map((c) => c.id === catId ? { ...c, dre_line: dreLine } : c));
      setSaved(catId);
      setTimeout(() => setSaved(null), 1500);
    }
    setSaving(null);
  }

  async function autoSuggest() {
    setSuggesting(true);
    // Heurística client-side baseada no nome
    const updates: Array<{ id: string; dre_line: DreLine }> = [];
    for (const cat of cats) {
      if (cat.dre_line) continue;
      const n = cat.name.toLowerCase();
      let line: DreLine | null = null;
      if (cat.kind === 'revenue') {
        line = 'revenue_gross';
      } else {
        if (/imposto|icms|iss|pis|cofins|simples|tribut/.test(n)) line = 'deduction_taxes';
        else if (/comiss|taxa.*market|fee|repasse/.test(n)) line = 'deduction_commissions';
        else if (/devolução|devolucao|abatimento/.test(n)) line = 'deduction_returns';
        else if (/custo.*mercadoria|cmv|cpv|produto/.test(n)) line = 'cogs';
        else if (/frete|marketing|ads|publicidade|embalagem/.test(n)) line = 'opex_sales';
        else if (/folha|pró-labore|salário|contabilidade|honorár|aluguel|energia|internet|software|saas/.test(n)) line = 'opex_admin';
        else if (/juros.*receita|aplicaç|rendimento/.test(n)) line = 'financial_revenue';
        else if (/juros|iof|tarifa.*banc|despesa.*financ/.test(n)) line = 'financial_expense';
        else if (/depreciaç|amortiz/.test(n)) line = 'depreciation';
        else if (/ir |csll|imposto.*renda/.test(n)) line = 'taxes_on_profit';
        else line = 'opex_other';
      }
      if (line) updates.push({ id: cat.id, dre_line: line });
    }

    try {
      for (const u of updates) {
        const { error } = await supabase.from('categories').update({ dre_line: u.dre_line } as never).eq('id', u.id);
        if (error) throw error;
      }
    } catch {
      setSuggesting(false);
      setSaveErr('Erro ao aplicar sugestões automáticas. Tente novamente.');
      return;
    }
    setSuggesting(false);
    await fetch();
    const count = updates.length;
    setSuggestMsg(count > 0 ? `${count} categoria(s) mapeada(s) automaticamente.` : 'Nenhuma categoria nova para mapear.');
    setTimeout(() => setSuggestMsg(null), 4000);
  }

  const revenue = cats.filter((c) => c.kind === 'revenue');
  const expense = cats.filter((c) => c.kind === 'expense');
  const unmapped = cats.filter((c) => !c.dre_line).length;

  return (
    <PageShell
      title="Mapeamento de categorias"
      subtitle="Atribua cada categoria a uma linha do DRE para gerar o relatório contábil."
      back="/financeiro/dre"
    >
      {fetchErr && (
        <div className="mb-4 px-4 py-3 rounded-xl border border-negative/30 bg-negative/5 text-sm text-negative">
          {fetchErr}
        </div>
      )}
      {saveErr && (
        <div className="mb-4 flex items-start justify-between gap-3 px-4 py-3 rounded-xl border border-negative/30 bg-negative/5 text-sm text-negative">
          <span>{saveErr}</span>
          <button onClick={() => setSaveErr(null)} className="shrink-0 text-negative/60 hover:text-negative">✕</button>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-ink-muted">
          {unmapped > 0
            ? <span className="text-warning font-medium">{unmapped} categoria(s) sem mapeamento</span>
            : <span className="text-positive font-medium">Todas as categorias mapeadas ✓</span>}
        </p>
        <button onClick={autoSuggest} disabled={suggesting || unmapped === 0} className="btn-secondary gap-2">
          {suggesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
          Sugerir automaticamente
        </button>
      </div>

      {suggestMsg && (
        <div className="mb-4 px-4 py-2.5 rounded-xl bg-positive/10 border border-positive/20 text-sm text-positive font-medium">
          ✓ {suggestMsg}
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[1,2,3,4,5].map((i) => <div key={i} className="h-12 rounded-xl bg-surface-subtle animate-pulse" />)}
        </div>
      ) : cats.length === 0 ? (
        <div className="card p-8">
          <EmptyState icon={Tag} title="Nenhuma categoria criada"
            description="Crie categorias na tela de transações e depois mapeie-as aqui." />
        </div>
      ) : (
        <div className="space-y-6">
          {[['Receitas', revenue], ['Despesas', expense]].map(([label, list]) => (
            <div key={label as string}>
              <h2 className="font-semibold text-ink mb-2">{label as string}</h2>
              <div className="card overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-ink-muted border-b border-border bg-surface-subtle/50">
                      <th className="px-5 py-2.5 text-left font-medium">Categoria</th>
                      <th className="px-5 py-2.5 text-left font-medium">Linha do DRE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(list as Category[]).map((cat) => {
                      const options = cat.kind === 'revenue' ? DRE_OPTIONS_REVENUE : DRE_OPTIONS_EXPENSE;
                      const isSaving = saving === cat.id;
                      const isSaved  = saved === cat.id;
                      return (
                        <tr key={cat.id} className="border-b border-border/50 last:border-0 hover:bg-surface-subtle/30">
                          <td className="px-5 py-2.5 text-ink">{cat.name}</td>
                          <td className="px-5 py-2.5">
                            <div className="flex items-center gap-2">
                              <select
                                value={cat.dre_line ?? ''}
                                onChange={(e) => updateDreLine(cat.id, (e.target.value as DreLine) || null)}
                                className="input !py-1 !text-xs flex-1 max-w-xs"
                                disabled={isSaving}
                              >
                                <option value="">— sem mapeamento —</option>
                                {options.map((o) => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </select>
                              {isSaving && <Loader2 className="w-3.5 h-3.5 animate-spin text-ink-muted shrink-0" />}
                              {isSaved  && <span className="text-positive text-xs shrink-0">✓</span>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </PageShell>
  );
}
